import type { Block, ContentAST } from '../../../../../shared/ast/astTypes'
import { resolveContentRange } from '../../../../../shared/ast/contentRange'
import { parseMarkdown } from '../../../../../shared/ast/markdownParser'
import { astToMarkdown } from '../../../../../shared/ast/markdownRenderer'
import type { ContentProposal } from '../../../../../shared/contentProposal'

export interface InlineProposalPlan {
  /** The gap follows this original block. Null means before the first block. */
  afterBlockId: string | null
  deletedBlockIds: string[]
  insertedMarkdown: string
  prefixMarkdown: string
  suffixMarkdown: string
  stale: boolean
  start: number
  end: number
}

function markdown(blocks: Block[], meta: ContentAST['meta']): string {
  return astToMarkdown({ version: 1, meta, blocks }, { includeMetadata: false })
}

/** Trim unchanged blocks so an insertion occupies the actual gap, without repeating its anchor. */
export function planInlineProposal(ast: ContentAST, proposal: ContentProposal): InlineProposalPlan {
  const range = resolveContentRange(ast, proposal.range)
  const original = range ? ast.blocks.slice(range.startIdx, range.endIdx) : []
  const stale = !range || markdown(original, ast.meta).trim() !== proposal.currentText.trim()
  if (stale) {
    return {
      afterBlockId: range ? (original[original.length - 1]?.id ?? null) : null,
      deletedBlockIds: [],
      insertedMarkdown: proposal.markdown,
      prefixMarkdown: '',
      suffixMarkdown: '',
      stale: true,
      start: range?.startIdx ?? -1,
      end: range?.endIdx ?? -1,
    }
  }
  const proposed = parseMarkdown(proposal.markdown).blocks
  const before = original.map((block) => markdown([block], ast.meta))
  const after = proposed.map((block) => markdown([block], ast.meta))
  let prefix = 0
  while (prefix < before.length && prefix < after.length && before[prefix] === after[prefix]) {
    prefix++
  }
  let suffix = 0
  while (
    suffix < before.length - prefix &&
    suffix < after.length - prefix &&
    before[before.length - 1 - suffix] === after[after.length - 1 - suffix]
  ) {
    suffix++
  }
  const deletionEnd = range.endIdx - suffix
  const anchorIndex = deletionEnd - 1
  return {
    afterBlockId: ast.blocks[anchorIndex]?.id ?? null,
    deletedBlockIds: original.slice(prefix, original.length - suffix).map((block) => block.id),
    insertedMarkdown: after.slice(prefix, after.length - suffix).join('\n\n'),
    prefixMarkdown: after.slice(0, prefix).join('\n\n'),
    suffixMarkdown: suffix ? after.slice(after.length - suffix).join('\n\n') : '',
    stale: false,
    start: range.startIdx,
    end: range.endIdx,
  }
}

export function editedProposalMarkdown(plan: InlineProposalPlan, insertion: string): string {
  return [plan.prefixMarkdown, insertion, plan.suffixMarkdown].filter((part) => part.trim()).join('\n\n')
}
